import React, { useState, useEffect } from 'react';
import {
  Paper,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Badge,
  Progress,
  Card,
  SimpleGrid,
  Loader,
  Center,
  Box,
  Alert,
  Timeline,
  ThemeIcon,
  Divider
} from '@mantine/core';
import { accountsAPI } from '../services/api';

function AccountDashboard({ onAddFunds }) {
  const [accountData, setAccountData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAccountData();
  }, []);

  const loadAccountData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [balanceResponse, transactionsResponse] = await Promise.all([
        accountsAPI.getBalance(),
        accountsAPI.getTransactions({ limit: 10 })
      ]);

      const balanceData = balanceResponse?.data || balanceResponse;
      const transactionsData = transactionsResponse?.data || transactionsResponse;

      setAccountData(balanceData);
      setTransactions(transactionsData?.transactions || transactionsResponse?.transactions || []);
    } catch (err) {
      console.error('Failed to load account data:', err);
      setError(err.message || 'Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'deposit': return 'teal';
      case 'refund': return 'teal';
      case 'allocation': return 'blue';
      default: return 'gray';
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return '💰';
      case 'refund': return '↩️';
      case 'allocation': return '📤';
      default: return '📝';
    }
  };

  if (loading) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text size="lg" c="dimmed">Loading account data...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error Loading Account" radius="md">
        <Text mb="md">{error}</Text>
        <Button onClick={loadAccountData} variant="light" color="red">
          Try Again
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="xl">
      {/* Account Balance Cards */}
      <Paper p="xl" radius="lg" withBorder>
        <Group justify="space-between" mb="xl">
          <Box>
            <Title order={2} size="h3" mb="xs">
              <Text
                component="span"
                style={{
                  background: 'linear-gradient(45deg, #10b981 0%, #059669 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                💳 Account Overview
              </Text>
            </Title>
          </Box>
          <Group gap="xs">
            <Button
              onClick={onAddFunds}
              variant="gradient"
              gradient={{ from: 'teal', to: 'green' }}
              leftSection="+"
            >
              Add Funds
            </Button>
            <Button onClick={loadAccountData} variant="light" color="gray">
              🔄 Refresh
            </Button>
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
          {/* Available Balance */}
          <Card withBorder radius="md" style={{ 
            background: 'linear-gradient(135deg, rgba(16,185,129,0.05) 0%, rgba(0,0,0,0) 100%)',
            borderColor: 'rgba(16,185,129,0.3)'
          }}>
            <Stack gap="xs">
              <Text size="60px" style={{ lineHeight: 1 }}>💰</Text>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Available Balance</Text>
              <Text size="xl" fw={700} c="teal">
                {formatCurrency(accountData?.available_balance)}
              </Text>
            </Stack>
          </Card>

          {/* Total Deposited */}
          <Card withBorder radius="md" style={{
            background: 'linear-gradient(135deg, rgba(59,130,246,0.05) 0%, rgba(0,0,0,0) 100%)',
            borderColor: 'rgba(59,130,246,0.3)'
          }}>
            <Stack gap="xs">
              <Text size="60px" style={{ lineHeight: 1 }}>📊</Text>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total Deposited</Text>
              <Text size="xl" fw={700} c="blue">
                {formatCurrency(accountData?.balance)}
              </Text>
            </Stack>
          </Card>

          {/* Allocated to Groups */}
          <Card withBorder radius="md" style={{
            background: 'linear-gradient(135deg, rgba(245,158,11,0.05) 0%, rgba(0,0,0,0) 100%)',
            borderColor: 'rgba(245,158,11,0.3)'
          }}>
            <Stack gap="xs">
              <Text size="60px" style={{ lineHeight: 1 }}>👥</Text>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Allocated to Groups</Text>
              <Text size="xl" fw={700} c="orange">
                {formatCurrency(accountData?.total_allocated)}
              </Text>
            </Stack>
          </Card>
        </SimpleGrid>
      </Paper>

      {/* Group Allocations */}
      <Paper p="xl" radius="lg" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3} size="h4">
            👥 Group Allocations
          </Title>
          <Badge size="lg" variant="light" color="blue">
            {accountData?.allocations?.length || 0} Active
          </Badge>
        </Group>

        {accountData?.allocations?.length > 0 ? (
          <Stack gap="md">
            {accountData.allocations.map((allocation) => {
              const percentage = accountData.balance > 0 ?
                Math.round((allocation.allocated_amount / accountData.balance) * 100) : 0;
              const spent = allocation.allocated_amount - allocation.remaining_amount;
              const spentPercentage = allocation.allocated_amount > 0 ?
                Math.round((spent / allocation.allocated_amount) * 100) : 0;

              return (
                <Card key={allocation.id} padding="lg" radius="md" withBorder>
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Box>
                        <Text size="lg" fw={600}>{allocation.group_name}</Text>
                        <Text size="xs" c="dimmed">Allocation #{allocation.id}</Text>
                      </Box>
                      <Box ta="right">
                        <Text size="xl" fw={700} c="blue">
                          {formatCurrency(allocation.allocated_amount)}
                        </Text>
                        <Badge size="sm" variant="light" color="blue">
                          {percentage}% of total
                        </Badge>
                      </Box>
                    </Group>

                    <Box>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" c="dimmed">Budget Usage</Text>
                        <Text size="sm" fw={500}>{spentPercentage}%</Text>
                      </Group>
                      <Progress
                        value={spentPercentage}
                        color={spentPercentage > 90 ? 'red' : spentPercentage > 70 ? 'orange' : 'blue'}
                        size="lg"
                        radius="xl"
                      />
                    </Box>

                    <Group justify="space-between">
                      <Group gap="xs">
                        <Text size="sm" c="teal" fw={600}>
                          ✓ Available: {formatCurrency(allocation.remaining_amount)}
                        </Text>
                      </Group>
                      <Text size="sm" c="dimmed">
                        Spent: {formatCurrency(spent)}
                      </Text>
                    </Group>
                  </Stack>
                </Card>
              );
            })}

            <Alert color="blue" variant="light" radius="md" title="Allocation Summary">
              Total allocated {formatCurrency(accountData.total_allocated)} across {accountData.allocations.length} groups.
              {accountData.available_balance > 0 && (
                <> You still have {formatCurrency(accountData.available_balance)} available to allocate.</>
              )}
            </Alert>
          </Stack>
        ) : (
          <Center py="xl">
            <Stack align="center" gap="md">
              <Text size="60px">👥</Text>
              <Title order={4}>No Active Allocations</Title>
              <Text c="dimmed" ta="center" maw={500}>
                Start by allocating funds to groups for collaborative gift purchasing
              </Text>
            </Stack>
          </Center>
        )}
      </Paper>

      {/* Recent Transactions */}
      <Paper p="xl" radius="lg" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3} size="h4">
            📝 Recent Transactions
          </Title>
          <Badge size="lg" variant="light" color="grape">
            {transactions.length} Total
          </Badge>
        </Group>

        {transactions.length > 0 ? (
          <Timeline active={transactions.length} bulletSize={32} lineWidth={2}>
            {transactions.map((transaction) => (
              <Timeline.Item
                key={transaction.id}
                bullet={
                  <ThemeIcon
                    size={32}
                    variant="gradient"
                    gradient={{ 
                      from: getTransactionColor(transaction.type), 
                      to: getTransactionColor(transaction.type) 
                    }}
                    radius="xl"
                  >
                    {getTransactionIcon(transaction.type)}
                  </ThemeIcon>
                }
                title={
                  <Group justify="space-between">
                    <Box>
                      <Badge
                        size="sm"
                        variant="light"
                        color={getTransactionColor(transaction.type)}
                        tt="capitalize"
                      >
                        {transaction.type}
                      </Badge>
                      <Text size="sm" mt="xs">{transaction.description}</Text>
                      <Text size="xs" c="dimmed" mt="xs">
                        {new Date(transaction.created_at).toLocaleString()}
                        {transaction.reference_id && ` • Ref: ${transaction.reference_id}`}
                      </Text>
                    </Box>
                    <Box ta="right">
                      <Text
                        size="xl"
                        fw={700}
                        c={getTransactionColor(transaction.type)}
                      >
                        {transaction.type === 'allocation' ? '-' : '+'}
                        {formatCurrency(Math.abs(transaction.amount))}
                      </Text>
                      <Text size="xs" c="dimmed" mt="xs">
                        Balance: {formatCurrency(transaction.balance_after)}
                      </Text>
                    </Box>
                  </Group>
                }
              />
            ))}
          </Timeline>
        ) : (
          <Center py="xl">
            <Stack align="center" gap="md">
              <Text size="60px">📝</Text>
              <Title order={4}>No Transactions Yet</Title>
              <Text c="dimmed" ta="center" maw={500}>
                Your account activity will appear here once you start using the funding system
              </Text>
            </Stack>
          </Center>
        )}
      </Paper>

      {/* Info Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
        <Alert
          color="blue"
          variant="light"
          radius="md"
          title="🔒 Transparent & Secure"
        >
          All transactions are tracked and auditable for complete transparency and security
        </Alert>

        <Alert
          color="teal"
          variant="light"
          radius="md"
          title="💰 Automatic Refunds"
        >
          Funds are automatically refunded if groups dissolve before purchases are made
        </Alert>
      </SimpleGrid>
    </Stack>
  );
}

export default AccountDashboard;
